import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// re: valida el número local (sin código de país). ex: ejemplo mostrado al usuario.
export const PHONE_COUNTRIES = [
  { code: "CL", dial: "56", flag: "🇨🇱", name: "Chile", max: 9, re: /^9\d{8}$/, ex: "9 1234 5678", hint: "9 dígitos y debe comenzar con 9" },
  { code: "AR", dial: "54", flag: "🇦🇷", name: "Argentina", max: 11, re: /^9?\d{10}$/, ex: "9 11 2345 6789", hint: "10 dígitos (con o sin 9 inicial)" },
  { code: "PE", dial: "51", flag: "🇵🇪", name: "Perú", max: 9, re: /^9\d{8}$/, ex: "912 345 678", hint: "9 dígitos y debe comenzar con 9" },
  { code: "BO", dial: "591", flag: "🇧🇴", name: "Bolivia", max: 8, re: /^[67]\d{7}$/, ex: "7123 4567", hint: "8 dígitos y debe comenzar con 6 o 7" },
  { code: "CO", dial: "57", flag: "🇨🇴", name: "Colombia", max: 10, re: /^3\d{9}$/, ex: "312 345 6789", hint: "10 dígitos y debe comenzar con 3" },
  { code: "EC", dial: "593", flag: "🇪🇨", name: "Ecuador", max: 9, re: /^9\d{8}$/, ex: "99 123 4567", hint: "9 dígitos y debe comenzar con 9" },
  { code: "UY", dial: "598", flag: "🇺🇾", name: "Uruguay", max: 8, re: /^9\d{7}$/, ex: "94 123 456", hint: "8 dígitos y debe comenzar con 9" },
  { code: "PY", dial: "595", flag: "🇵🇾", name: "Paraguay", max: 9, re: /^9\d{8}$/, ex: "981 123 456", hint: "9 dígitos y debe comenzar con 9" },
  { code: "VE", dial: "58", flag: "🇻🇪", name: "Venezuela", max: 10, re: /^4\d{9}$/, ex: "412 123 4567", hint: "10 dígitos y debe comenzar con 4" },
  { code: "BR", dial: "55", flag: "🇧🇷", name: "Brasil", max: 11, re: /^\d{2}9\d{8}$/, ex: "11 91234 5678", hint: "11 dígitos (área + 9 + 8 dígitos)" },
  { code: "MX", dial: "52", flag: "🇲🇽", name: "México", max: 10, re: /^\d{10}$/, ex: "55 1234 5678", hint: "10 dígitos" },
  { code: "ES", dial: "34", flag: "🇪🇸", name: "España", max: 9, re: /^[67]\d{8}$/, ex: "612 345 678", hint: "9 dígitos y debe comenzar con 6 o 7" },
  { code: "US", dial: "1", flag: "🇺🇸", name: "EE.UU. / Canadá", max: 10, re: /^\d{10}$/, ex: "202 555 0123", hint: "10 dígitos" },
];

const TZ_COUNTRY = {
  "America/Santiago": "CL", "America/Punta_Arenas": "CL", "Pacific/Easter": "CL", "America/Lima": "PE",
  "America/La_Paz": "BO", "America/Bogota": "CO", "America/Guayaquil": "EC", "America/Montevideo": "UY",
  "America/Asuncion": "PY", "America/Caracas": "VE", "America/Sao_Paulo": "BR", "Europe/Madrid": "ES",
};

export function detectCountry() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (TZ_COUNTRY[tz]) return TZ_COUNTRY[tz];
    if (tz.startsWith("America/Argentina")) return "AR";
    if (tz.startsWith("America/Mexico") || ["America/Monterrey", "America/Cancun", "America/Merida", "America/Tijuana"].includes(tz)) return "MX";
    const region = (navigator.language || "").split("-")[1]?.toUpperCase();
    if (region && PHONE_COUNTRIES.some((c) => c.code === region)) return region;
    if (tz.startsWith("America/") && ["America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Toronto"].includes(tz)) return "US";
  } catch { /* sin detección */ }
  return "CL";
}

function split(e164) {
  const d = String(e164 || "").replace(/\D/g, "");
  if (!d) return null;
  const c = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length).find((x) => d.startsWith(x.dial));
  return c ? { c, local: d.slice(c.dial.length) } : null;
}

export function isValidPhone(e164) {
  const p = split(e164);
  return !!p && p.c.re.test(p.local);
}

export function formatPhoneDisplay(e164) {
  const p = split(e164);
  return p ? `+${p.c.dial} ${groupDigits(p.local)}` : e164 || "";
}

function groupDigits(local) {
  const d = local.replace(/\D/g, "");
  if (d.length <= 4) return d;
  if (d.length === 9 && d[0] === "9") return `${d[0]} ${d.slice(1, 5)} ${d.slice(5)}`;
  return d.replace(/(\d{3,4})(?=(\d{4})+$)/g, "$1 ").replace(/(\d{4})(?=\d)/g, "$1 ").trim();
}

export function PhoneInput({ value, onChange, testId = "phone-input", disabled }) {
  const parsed = useMemo(() => split(value), [value]);
  const [code, setCode] = useState(() => parsed?.c.code || detectCountry());
  useEffect(() => { if (parsed && parsed.c.code !== code) setCode(parsed.c.code); }, [parsed]); // eslint-disable-line
  const c = PHONE_COUNTRIES.find((x) => x.code === code) || PHONE_COUNTRIES[0];
  const local = parsed && parsed.c.code === code ? parsed.local : "";
  const showError = local.length > 0 && !c.re.test(local);

  const handle = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, c.max);
    onChange(digits ? `+${c.dial}${digits}` : "");
  };
  const changeCountry = (k) => {
    const n = PHONE_COUNTRIES.find((x) => x.code === k);
    setCode(k);
    onChange(local ? `+${n.dial}${local.slice(0, n.max)}` : "");
  };

  return (
    <div data-testid={`${testId}-wrap`}>
      <div className="flex gap-2">
        <Select value={c.code} onValueChange={changeCountry} disabled={disabled}>
          <SelectTrigger className="w-[112px] shrink-0" data-testid={`${testId}-country`} aria-label="País">
            <SelectValue>{c.flag} +{c.dial}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {PHONE_COUNTRIES.map((x) => (
              <SelectItem key={x.code} value={x.code} data-testid={`${testId}-country-${x.code}`}>{x.flag} {x.name} +{x.dial}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input inputMode="numeric" autoComplete="tel-national" value={groupDigits(local)} onChange={handle} disabled={disabled}
          data-testid={testId} placeholder={c.ex} className="flex-1" aria-invalid={showError} />
      </div>
      {showError && (
        <p className="text-xs text-destructive mt-1.5" data-testid={`${testId}-error`}>
          Celular de {c.name} inválido: {c.hint} (ej: {c.ex})
        </p>
      )}
    </div>
  );
}
